<template>
  <div
    class="status-indicator"
    :class="{ 'status-indicator--with-separator': showSeparator }"
    :style="containerStyle"
    data-testid="status-indicator"
  >
    <div class="status-indicator__content" :class="sizePaddingClass">
      <div
        class="status-indicator__square"
        :class="squareSizeClass"
        :style="indicatorStyle"
        aria-hidden="true"
      ></div>
      <span class="status-indicator__label" :class="labelSizeClass">
        {{ effectiveLabel }}
      </span>
    </div>
  </div>
</template>

<script>
import { STATUS_CONFIG, STATUS_VARIANTS, DEFAULT_STATUS_VARIANT } from '../utils/statusConstants';

export default {
  name: 'StatusIndicator',
  props: {
    variant: {
      type: String,
      required: true,
      validator(value) {
        return Object.values(STATUS_VARIANTS).includes(value);
      },
    },
    customLabel: {
      type: String,
      default: '',
    },
    showSeparator: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
      validator(value) {
        return ['sm', 'md', 'lg'].includes(value);
      },
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    cornerRadius: {
      type: String,
      default: '',
    },
  },
  computed: {
    /**
     * Resolve the configuration for the provided status variant.
     */
    variantConfig() {
      return STATUS_CONFIG[this.variant] || STATUS_CONFIG[DEFAULT_STATUS_VARIANT];
    },
    /**
     * Determine the fallback-aware label used by the component.
     */
    effectiveLabel() {
      if (this.customLabel) {
        return this.customLabel;
      }
      return this.variantConfig.label;
    },
    /**
     * Inline style for the colored indicator.
     */
    indicatorStyle() {
      return {
        backgroundColor: this.variantConfig.color,
        boxShadow: `0 4px 12px ${this.variantConfig.shadow}`,
      };
    },
    /**
     * Apply optional outlined styling so consumers can display a bordered chip.
     */
    containerStyle() {
      if (!this.outlined) {
        return {};
      }
      return {
        border: `1px solid ${this.variantConfig.color}`,
        borderRadius: this.cornerRadius || '100px',
        padding: '4px 10px',
        width: 'fit-content',
        display: 'inline-flex',
        flexDirection: 'column',
        backgroundColor: 'transparent',
      };
    },
    /**
     * Padding class based on size selection.
     */
    sizePaddingClass() {
      switch (this.size) {
        case 'sm':
          return 'status-indicator__content--sm';
        case 'lg':
          return 'status-indicator__content--lg';
        default:
          return 'status-indicator__content--md';
      }
    },
    /**
     * Square size modifier class.
     */
    squareSizeClass() {
      return `status-indicator__square--${this.size}`;
    },
    /**
     * Label size modifier class.
     */
    labelSizeClass() {
      return `status-indicator__label--${this.size}`;
    },
  },
};
</script>

<style scoped>
.status-indicator {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.status-indicator--with-separator {
  border-bottom: 1px solid rgba(147, 51, 234, 0.25);
}

.status-indicator__content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-indicator__square {
  flex-shrink: 0;
  border-radius: 4px;
}

.status-indicator__square--sm {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}

.status-indicator__square--md {
  width: 14px;
  height: 14px;
  border-radius: 4px;
}

.status-indicator__square--lg {
  width: 16px;
  height: 16px;
  border-radius: 6px;
}

.status-indicator__label {
  letter-spacing: -0.01em;
  font-weight: 600;
}

</style>
